import type { PortIpAccessControlRule } from '@/types';

export const MAX_IP_RULES = 10;
export const MAX_CIDRS_PER_RULE = 10;
export const MAX_SERVICE_TAGS_PER_RULE = 2;
export const SUPPORTED_SERVICE_TAG = 'AzureMachineLearning';

export function validateCidr(value: string): string | null {
  const parts = value.split('/');
  const address = parts[0];
  const prefix = parts[1];
  const error = 'Must be a valid IPv4 or IPv6 CIDR (e.g., 10.0.0.0/8 or 2001:db8::/32)';
  if (parts.length !== 2 || !/^\d{1,3}$/.test(prefix)) return error;

  if (address.includes(':')) {
    if (!/^[0-9a-fA-F:.]+$/.test(address) || Number(prefix) > 128) return error;
    try {
      // The URL parser validates IPv6 compression and embedded IPv4 addresses.
      new URL(`http://[${address}]`);
      return null;
    } catch {
      return error;
    }
  }

  const octets = address.split('.');
  return octets.length === 4 &&
    octets.every((octet) => /^\d{1,3}$/.test(octet) && Number(octet) <= 255) &&
    Number(prefix) <= 32
    ? null
    : error;
}

export function validateServiceTag(value: string): string | null {
  return value.toLowerCase() === SUPPORTED_SERVICE_TAG.toLowerCase()
    ? null
    : `Unsupported service tag "${value}". Currently supported: ${SUPPORTED_SERVICE_TAG}.`;
}

export function validateIpRuleSelectors(rule: PortIpAccessControlRule): string | null {
  const cidrs = rule.sourceCidrs ?? [];
  const tags = rule.sourceServiceTags ?? [];
  const hasCidrs = cidrs.length > 0;
  const hasServiceTags = tags.length > 0;
  if (hasCidrs === hasServiceTags) {
    return 'Choose exactly one nonempty source: CIDR ranges or service tags.';
  }
  if (cidrs.length > MAX_CIDRS_PER_RULE) {
    return `Maximum ${MAX_CIDRS_PER_RULE} explicit CIDRs per rule.`;
  }
  if (tags.length > MAX_SERVICE_TAGS_PER_RULE) {
    return `Maximum ${MAX_SERVICE_TAGS_PER_RULE} distinct service tags per rule.`;
  }
  if (new Set(tags.map((tag) => tag.toLowerCase())).size !== tags.length) {
    return 'Service tag names must be unique (case-insensitive).';
  }
  return (
    cidrs.map(validateCidr).find(Boolean) ?? tags.map(validateServiceTag).find(Boolean) ?? null
  );
}

export function validateIpAccessControlRules(rules: PortIpAccessControlRule[]): string | null {
  if (rules.length > MAX_IP_RULES) return `Maximum ${MAX_IP_RULES} IP rules per port.`;
  for (const [index, rule] of rules.entries()) {
    const error = validateIpRuleSelectors(rule);
    if (error) return `Rule ${index + 1}: ${error}`;
  }
  return null;
}
